/**
 * The tracker's one number formatter.
 *
 * Every figure on every screen — the campaigns list, the card, the report, the
 * strip on «Сегодня», the CSV — goes through here, so «27 146», «5,1%»,
 * «+1 730» and «+9 п.п.» are spelled one way everywhere (доска Metrics:
 * «подписывается одинаково в списке, карточке, отчёте, выгрузке и на
 * телефоне»). Russian grouping and decimal comma, no currency sign: the
 * footer says once that the money is in USD, and a «$» in front of every cell
 * of a fourteen-column table is noise.
 *
 * Absent is not zero. Every function takes `undefined`/`null`/NaN and returns
 * an em dash: a ratio with a zero denominator has no value, and printing 0
 * would be a claim the server never made.
 */

const LOCALE = "ru-RU";
export const DASH = "—";

type Num = number | null | undefined;

function finite(v: Num): v is number {
  return typeof v === "number" && Number.isFinite(v);
}

const cache = new Map<string, Intl.NumberFormat>();
function nf(digits: number, signed: boolean): Intl.NumberFormat {
  const key = `${digits}:${signed}`;
  let f = cache.get(key);
  if (!f) {
    f = new Intl.NumberFormat(LOCALE, {
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
      signDisplay: signed ? "exceptZero" : "auto",
    });
    cache.set(key, f);
  }
  return f;
}

/** A count: «27 146». */
export function formatInt(v: Num): string {
  return finite(v) ? nf(0, false).format(Math.round(v)) : DASH;
}

/**
 * Money. Totals (доход, расход, профит) are whole: «11 586». A price per unit
 * (CPA, EPC, CPC, CPL) keeps two decimals — rounding EPC to a whole number
 * prints 0 for every campaign.
 */
export function formatMoney(v: Num, opts: { unit?: boolean; signed?: boolean } = {}): string {
  if (!finite(v)) return DASH;
  // Three decimals for a unit price under ten cents: a CPC of 0,004 and one
  // of 0,009 are different businesses, and two decimals print both as 0,00.
  const digits = opts.unit ? (v !== 0 && Math.abs(v) < 0.1 ? 3 : 2) : 0;
  return nf(digits, !!opts.signed).format(v);
}

/** A ratio as a percent: 0.051 → «5,1%». `digits` is per metric (CR 1, апрув 0). */
export function formatPct(ratio: Num, opts: { digits?: number; signed?: boolean } = {}): string {
  return finite(ratio) ? `${nf(opts.digits ?? 1, !!opts.signed).format(ratio * 100)}%` : DASH;
}

/** A difference of two percents, in percentage points: «+9 п.п.». Takes ratios. */
export function formatPp(deltaRatio: Num): string {
  return finite(deltaRatio) ? `${nf(0, true).format(deltaRatio * 100)} п.п.` : DASH;
}

/** Relative change, «+9%». Absent when the previous value is zero or missing. */
export function relativeChange(current: Num, previous: Num): number | undefined {
  if (!finite(current) || !finite(previous) || previous === 0) return undefined;
  return (current - previous) / Math.abs(previous);
}

export function formatChange(current: Num, previous: Num): string {
  const r = relativeChange(current, previous);
  return r === undefined ? DASH : formatPct(r, { digits: 0, signed: true });
}

/** How long ago, in the words the footer uses: «3 с», «12 мин», «2 ч», «4 дн». */
export function formatAge(seconds: Num): string {
  if (!finite(seconds) || seconds < 0) return DASH;
  if (seconds < 60) return `${Math.round(seconds)} с`;
  if (seconds < 3600) return `${Math.round(seconds / 60)} мин`;
  if (seconds < 86_400) return `${Math.round(seconds / 3600)} ч`;
  return `${Math.round(seconds / 86_400)} дн`;
}

/** «08.10 – 14.10» from two ISO days. Pure. */
export function formatDayRange(from: string, to: string): string {
  const d = (iso: string) => (/^\d{4}-\d{2}-\d{2}$/.test(iso) ? `${iso.slice(8, 10)}.${iso.slice(5, 7)}` : iso);
  return from === to ? d(from) : `${d(from)} – ${d(to)}`;
}

/** Tone of a signed number, for the green/red ink. */
export function toneOf(v: Num): "pos" | "neg" | undefined {
  if (!finite(v) || v === 0) return undefined;
  return v > 0 ? "pos" : "neg";
}

/**
 * Money in a currency that is not the report's — an offer's payout in EUR, a
 * source's stamped cost in its own currency: «12,50 €». Reports stay in USD
 * and use `formatMoney`; this is for a price as the partner states it.
 */
export function formatAmount(v: Num, currency: string): string {
  if (!finite(v)) return DASH;
  try {
    return new Intl.NumberFormat(LOCALE, { style: "currency", currency, maximumFractionDigits: 2 }).format(v);
  } catch {
    // An unknown ISO code throws; the number with the code beside it is still true.
    return `${nf(2, false).format(v)} ${currency}`;
  }
}
